import { useCallback, useEffect, useMemo, useState } from 'react'
import AuditView from './AuditView'
import { Center } from '../../model/domain/center'
import { UserAudit, AuditType } from '../../model/domain/audit'
import TaskAudit from '../../ws/TaskAudit'

interface AuditContainerState {
    centers?: Center[]
    initialLoading: boolean
    centerId: number | null
    type: AuditType
    startDate: Date
    endDate: Date
    email: string
    loadingResults: boolean
    audits?: UserAudit[]
    error: string | null
}

const today = new Date()
const oneWeekAgo = new Date()
oneWeekAgo.setDate(today.getDate() - 7)

export default function AuditContainer() {
    const [state, setState] = useState<AuditContainerState>({
        initialLoading: false,
        centerId: null,
        type: AuditType.LOGIN,
        startDate: oneWeekAgo,
        endDate: today,
        email: '',
        loadingResults: false,
        error: null,
    })

    const updateState = (changes: Partial<AuditContainerState>) => {
        setState((state) => ({
            ...state,
            ...changes,
        }))
    }

    const task = useMemo(() => new TaskAudit(), [])

    const fetchCenters = useCallback(async () => {
        try {
            updateState({ initialLoading: true })
            const response = await task.getCenters()
            const centers = response.data
            updateState({ centers })
        } catch (err) {
            if (err instanceof Error) {
                updateState({ error: err.message })
            }
        } finally {
            updateState({ initialLoading: false })
        }
    }, [task])

    useEffect(() => {
        fetchCenters()
    }, [fetchCenters])

    const {
        centers,
        initialLoading,
        centerId,
        type,
        startDate,
        endDate,
        email,
        loadingResults,
        audits,
        error,
    } = state

    const fetchAudits = useCallback(async () => {
        try {
            updateState({ loadingResults: true, error: null })
            const audits = await task.getAudits(
                startDate,
                endDate,
                type,
                centerId,
                email
            )
            updateState({ audits })
        } catch (err) {
            if (err instanceof Error) {
                updateState({ error: err.message })
            }
        } finally {
            updateState({ loadingResults: false })
        }
    }, [task, centerId, type, startDate, endDate, email])

    const handleChangeCenterId = (centerId: number | null) => {
        updateState({ centerId })
    }

    const handleChangeType = (type: AuditType) => {
        updateState({ type })
    }

    const handleChangeStartDate = (startDate: string) => {
        updateState({ startDate: new Date(startDate) })
    }

    const handleChangeEndDate = (endDate: string) => {
        updateState({ endDate: new Date(endDate) })
    }

    const handleChangeEmail = (email: string) => {
        updateState({ email })
    }

    const handleFilter = () => {
        fetchAudits()
    }

    return (
        <AuditView
            centers={centers}
            initialLoading={initialLoading}
            centerId={centerId}
            type={type}
            startDate={startDate}
            endDate={endDate}
            email={email}
            loadingResults={loadingResults}
            audits={audits}
            error={error}
            onChangeCenterId={handleChangeCenterId}
            onChangeType={handleChangeType}
            onChangeStartDate={handleChangeStartDate}
            onChangeEndDate={handleChangeEndDate}
            onChangeEmail={handleChangeEmail}
            onFilter={handleFilter}
        />
    )
}
